<svelte:options runes={true} />

<script lang="ts">
  import { greaterThan } from "dinero.js";
  import { coerceMoney, formatMoney, formatSaveBadgeText } from "~/lib/money";
  import Badge from "~/components/ui/Badge/Badge.svelte";

  import type { ClassValue } from "svelte/elements";
  import type { BadgeColor } from "~/components/ui/Badge/Badge.svelte";
  import type { MoneyInput } from "~/lib/money";

  interface Props {
    price: MoneyInput;
    compareAtPrice?: MoneyInput;
    prefix?: string;
    saveBadge?: boolean;
    saveBadgeColor?: BadgeColor;
    saveBadgePrefix?: string;
    class?: ClassValue;
  }

  let {
    price,
    compareAtPrice,
    prefix = "",
    saveBadge = true,
    saveBadgeColor = "primary",
    saveBadgePrefix = "",
    class: className = "",
  }: Props = $props();

  let normalizedPrice = $derived(coerceMoney(price));
  let normalizedCompareAtPrice = $derived(
    compareAtPrice != null ? coerceMoney(compareAtPrice) : undefined,
  );
  let showComparePrice = $derived(
    normalizedCompareAtPrice != null && greaterThan(normalizedCompareAtPrice, normalizedPrice),
  );
  let saveBadgeText = $derived(formatSaveBadgeText(price, compareAtPrice, saveBadgePrefix));
</script>

<div class={["da-price", className]}>
  <span class="da-price__group">
    {#if prefix}
      <span class="da-price__prefix">{prefix}</span>
    {/if}
    <span class={["da-price__sale", showComparePrice && "da-price__sale--with-compare"]}>
      {formatMoney(normalizedPrice)}
    </span>
    {#if showComparePrice}
      <span class="da-price__compare">
        {formatMoney(normalizedCompareAtPrice!)}
      </span>
    {/if}
  </span>
  {#if saveBadge && saveBadgeText}
    <Badge text={saveBadgeText} color={saveBadgeColor} size="sm" class="da-price__save-badge" />
  {/if}
</div>

<style>
  @layer aurora {
    .da-price {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: var(--da-space-1);
      font-variant-numeric: tabular-nums;
    }

    .da-price__group {
      display: flex;
      align-items: baseline;
      gap: var(--da-space-1);
    }

    .da-price__prefix {
      font-size: var(--da-font-size-xs);
      line-height: var(--da-line-height-xs);
      font-weight: var(--da-font-weight-medium);
      color: var(--da-color-text-muted);
    }

    .da-price__sale {
      color: var(--da-color-text-sale);
      font-size: var(--da-price-font-size, var(--da-font-size-sm));
      line-height: var(--da-price-line-height, var(--da-line-height-sm));
      font-weight: var(--da-font-weight-bold);
    }

    .da-price__compare {
      font-size: var(--da-price-compare-font-size, var(--da-font-size-xs));
      line-height: var(--da-price-compare-line-height, var(--da-line-height-xs));
      font-weight: var(--da-font-weight-medium);
      color: var(--da-color-charcoal-300);
      text-decoration-line: line-through;
    }

    @container (width >= 300px) {
      .da-price {
        gap: var(--da-space-1);
      }

      .da-price__prefix {
        font-size: var(--da-font-size-sm);
        line-height: var(--da-line-height-sm);
      }

      .da-price__sale {
        font-size: var(--da-price-font-size, var(--da-font-size-xl));
        line-height: var(--da-price-line-height, var(--da-line-height-xl));
      }

      .da-price__compare {
        font-size: var(--da-price-compare-font-size, var(--da-font-size-sm));
        line-height: var(--da-price-compare-line-height, var(--da-line-height-sm));
        font-weight: var(--da-font-weight-semibold);
      }
    }
  }
</style>
